import { Component } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
// Removed MDDatePicker import

import { request } from "app/network";

interface AddPointLimitPolicyDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    editPolicy?: any | null;
    darkMode?: boolean;
}

interface AddPointLimitPolicyDialogState {
    policyName: string;
    limitCode: string;
    limitValue: string;
    description: string;
    isActive: boolean;
    isDefault: boolean;
    loading: boolean;
    errors: {
        policyName?: string;
        limitCode?: string;
        limitValue?: string;
    };
    limitCodeOptions: Array<{ code: string; name: string }>;
}

class AddPointLimitPolicyDialog extends Component<AddPointLimitPolicyDialogProps, AddPointLimitPolicyDialogState> {
    constructor(props: AddPointLimitPolicyDialogProps) {
        super(props);
        this.state = {
            policyName: "",
            limitCode: "",
            limitValue: "",
            description: "",
            isActive: true,
            isDefault: true,
            loading: false,
            errors: {},
            limitCodeOptions: [],
        };
    }

    componentDidMount() {
        this.loadLimitCodeOptions();
    }

    loadLimitCodeOptions = async () => {
        try {
            const res = await request("CommonCode.getByGroup", {
                paramz: {},
                pathParams: { groupCode: "POINT_LIMIT_POLICY" },
            });
            if (res && res.data) {
                const limitCodeOptions = res.data.map((item: any) => ({
                    code: item.code,
                    name: item.name,
                }));
                this.setState({ limitCodeOptions });
            }
        } catch (error) {
            console.error("제한 코드 옵션 로드 실패:", error);
        }
    };

    componentDidUpdate(prevProps: AddPointLimitPolicyDialogProps) {
        if (this.props.open && !prevProps.open) {
            // Reload options when dialog opens, in case they changed (optional, but good practice)
            if (this.state.limitCodeOptions.length === 0) {
                this.loadLimitCodeOptions();
            }

            if (this.props.editPolicy) {
                this.setState({
                    policyName: this.props.editPolicy.policyName || "",
                    limitCode: this.props.editPolicy.limitCode || "",
                    limitValue: this.props.editPolicy.limitValue?.toString() || "",
                    description: this.props.editPolicy.description || "",
                    isActive: this.props.editPolicy.isActive ?? true,
                    isDefault: this.props.editPolicy.isDefault ?? false,
                    errors: {},
                });
            } else {
                this.setState({
                    policyName: "",
                    limitCode: "",
                    limitValue: "",
                    description: "",
                    isActive: true,
                    isDefault: true,
                    errors: {},
                });
            }
        }
    }

    validate = () => {
        const errors: any = {};

        if (!this.state.policyName.trim()) {
            errors.policyName = "정책명을 입력해주세요.";
        }

        if (!this.state.limitCode.trim()) {
            errors.limitCode = "제한 코드를 선택해주세요.";
        }

        if (!this.state.limitValue.trim()) {
            errors.limitValue = "한도 값을 입력해주세요.";
        } else if (isNaN(Number(this.state.limitValue)) || Number(this.state.limitValue) < 0) {
            errors.limitValue = "올바른 숫자를 입력해주세요.";
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    };

    handleSubmit = async () => {
        if (!this.validate()) return;

        try {
            this.setState({ loading: true });

            const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
            const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);

            const data = {
                policyName: this.state.policyName.trim(),
                limitCode: this.state.limitCode.trim(),
                limitValue: parseInt(this.state.limitValue, 10),
                description: this.state.description.trim() || null,
                isActive: this.state.isActive,
                isDefault: this.state.isDefault,
                tenantSeqId: savedTenantId ? parseInt(savedTenantId, 10) : 1,
            };

            if (this.props.editPolicy) {
                await request("PointLimitPolicy.update", {
                    paramz: { ...data, seqId: this.props.editPolicy.seqId },
                    pathParams: { id: this.props.editPolicy.seqId }
                });
            } else {
                await request("PointLimitPolicy.create", { paramz: data });
            }

            this.setState({ loading: false });
            this.props.onSuccess();
            this.props.onClose();
        } catch (error: any) {
            console.error("정책 저장 실패:", error);
            alert(error?.response?.data?.message || "저장에 실패했습니다.");
            this.setState({ loading: false });
        }
    };

    render() {
        const { open, onClose, editPolicy, darkMode } = this.props;
        const { policyName, limitCode, limitValue, description, isActive, isDefault, loading, errors, limitCodeOptions } = this.state;

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#1a2035" : "#ffffff",
                        backgroundImage: "none",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        color: darkMode ? "#ffffff" : "inherit",
                        fontWeight: "medium",
                        fontSize: "1.25rem",
                    }}
                >
                    {editPolicy ? "적립 한도정책 수정" : "적립 한도정책 추가"}
                </DialogTitle>
                <DialogContent>
                    <MDBox component="form" role="form" pt={2}>
                        <MDBox mb={2}>
                            <MDInput
                                type="text"
                                label="정책명"
                                fullWidth
                                value={policyName}
                                onChange={(e: any) => this.setState({ policyName: e.target.value })}
                                error={!!errors.policyName}
                            />
                            {errors.policyName && (
                                <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                                    {errors.policyName}
                                </MDTypography>
                            )}
                        </MDBox>

                        <MDBox mb={2}>
                            <Autocomplete
                                fullWidth
                                disablePortal
                                options={limitCodeOptions}
                                getOptionLabel={(option: any) => `${option.name} (${option.code})`}
                                isOptionEqualToValue={(option: any, value: any) => option.code === value.code}
                                value={limitCodeOptions.find(opt => opt.code === limitCode) || null}
                                onChange={(_, newValue: any) => {
                                    this.setState({
                                        limitCode: newValue?.code || "",
                                        errors: { ...errors, limitCode: "" }
                                    });
                                }}
                                disabled={loading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="제한 코드 Type"
                                        error={!!errors.limitCode}
                                        helperText={errors.limitCode}
                                    />
                                )}
                                renderOption={(props, option) => {
                                    // Remove warning: "A props object containing a "key" prop is being spread into JSX"
                                    const { key, ...otherProps } = props as any;
                                    return (
                                        <li key={key} {...otherProps}>
                                            {option.name} ({option.code})
                                        </li>
                                    );
                                }}
                                sx={{
                                    '& .MuiAutocomplete-popupIndicator': {
                                        color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                    },
                                    '& .MuiAutocomplete-clearIndicator': {
                                        color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                    },
                                    // Fix TextField styles for dark mode
                                    '& .MuiInputBase-root': {
                                        color: darkMode ? '#ffffff' : 'inherit',
                                        '& fieldset': {
                                            borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? '#ffffff' : 'inherit',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                    },
                                }}
                            />
                        </MDBox>

                        <MDBox mb={2}>
                            <MDInput
                                type="number"
                                label="한도 값"
                                fullWidth
                                value={limitValue}
                                onChange={(e: any) => this.setState({ limitValue: e.target.value })}
                                error={!!errors.limitValue}
                                sx={{
                                    '& input[type=number]': {
                                        colorScheme: darkMode ? 'dark' : 'normal',
                                    },
                                }}
                            />
                            {errors.limitValue && (
                                <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                                    {errors.limitValue}
                                </MDTypography>
                            )}
                        </MDBox>

                        <MDBox mb={2}>
                            <MDInput
                                type="text"
                                label="설명 (선택)"
                                fullWidth
                                multiline
                                rows={3}
                                value={description}
                                onChange={(e: any) => this.setState({ description: e.target.value })}
                            />
                        </MDBox>

                        <MDBox mb={2}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isActive}
                                        onChange={(e) => this.setState({ isActive: e.target.checked })}
                                    />
                                }
                                label="활성화"
                            />
                        </MDBox>

                        <MDBox mb={2}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isDefault}
                                        onChange={(e) => this.setState({ isDefault: e.target.checked })}
                                    />
                                }
                                label="기본 정책으로 설정"
                            />
                            <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                                * 기본정책으로 설정하면 모든 사용자에게 적용됩니다.
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={onClose} color="secondary" disabled={loading}>
                        취소
                    </MDButton>
                    <MDButton onClick={this.handleSubmit} color="info" disabled={loading}>
                        {loading ? "저장 중..." : editPolicy ? "수정" : "추가"}
                    </MDButton>
                </DialogActions>
            </Dialog>
        );
    }
}

export default AddPointLimitPolicyDialog;
